import React from 'react';

import {
  AccountBalance as AccountBalanceIcon,
  Group as GroupIcon,
  Info as InfoIcon,
  ManageAccounts as ManageAccountsIcon,
} from '@mui/icons-material';
import { Box } from '@mui/material';

import { appRoutes, RouterEntitiesId } from '@clearverify/constants';

import { DEFAULT_AVATAR } from 'src/assets';
import { ItemDetailsLayout } from 'src/components/common';
import { useEmployerById, useEmployerLogo, useEmployerParamId } from 'src/hooks/employer';
import { useCurrentUser } from 'src/hooks/user';
import { MenuItem } from 'src/types/menu';
import { buildUrl } from 'src/utils/router';

export const EmployersDetailLayoutPEOPayrollProviderStaff: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const employerId = useEmployerParamId();
  const { employer, loading: employerLoading } = useEmployerById(employerId);
  const { employerLogo, employerLogoLoading, updateEmployerLogo, employerLogoUpdating } =
    useEmployerLogo(employerId);

  const { isPartnershipCompanyUser, isUserUpdating } = useCurrentUser();

  const builtRoutes = React.useMemo(
    () => ({
      companyInformation: buildUrl(appRoutes.employers.detail.companyInformation, {
        [RouterEntitiesId.employer]: employerId,
      }),
      disclaimers: buildUrl(appRoutes.employers.detail.disclaimers, {
        [RouterEntitiesId.employer]: employerId,
      }),
      staff: buildUrl(appRoutes.employers.detail.staff, {
        [RouterEntitiesId.employer]: employerId,
      }),
      employees: buildUrl(appRoutes.employers.detail.employees, {
        [RouterEntitiesId.employer]: employerId,
      }),
    }),
    [employerId],
  );

  const menuItems: Array<MenuItem> = React.useMemo(
    () => [
      {
        link: builtRoutes.companyInformation,
        title: 'Company Information',
        icon: AccountBalanceIcon,
      },
      {
        link: builtRoutes.disclaimers,
        title: 'Employer Disclaimer',
        icon: InfoIcon,
      },
      {
        link: builtRoutes.staff,
        title: 'Staff',
        icon: ManageAccountsIcon,
      },
      {
        link: builtRoutes.employees,
        title: 'Employees',
        icon: GroupIcon,
        hide: isPartnershipCompanyUser,
      },
    ],
    [builtRoutes, isPartnershipCompanyUser],
  );

  return (
    <ItemDetailsLayout
      name={employer?.displayName}
      logoUrl={employerLogo?.downloadUrl || DEFAULT_AVATAR}
      menuItems={menuItems}
      logoProps={{
        onChange: updateEmployerLogo,
        loading: employerLogoLoading || employerLogoUpdating,
      }}
      loading={employerLoading || isUserUpdating}
    >
      <Box>{children}</Box>
    </ItemDetailsLayout>
  );
};
